import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { Trash2, Mail, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AdminSubmissions() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contact-submissions");
      setList(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const markRead = async (s) => {
    try {
      await api.put(`/admin/contact-submissions/${s.id}/read`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const del = async (s) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await api.delete(`/admin/contact-submissions/${s.id}`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const unread = list.filter((s) => !s.read).length;

  return (
    <div className="p-6 md:p-10" data-testid="admin-submissions">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="font-script text-3xl text-blush-400">from your inbox</span>
          <h1 className="font-heading text-3xl md:text-4xl text-ink-900">
            Contact submissions{" "}
            {unread > 0 && (
              <span className="ml-3 text-sm align-middle bg-blush-600 text-white px-3 py-1 rounded-full font-body">
                {unread} new
              </span>
            )}
          </h1>
          <p className="mt-2 font-body text-ink-500 text-sm" data-testid="submissions-total">
            {list.length} {list.length === 1 ? "message" : "messages"} total · auto-refreshes every 30s
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="submissions-refresh"
          className="btn-ghost-rose text-xs inline-flex items-center gap-2 disabled:opacity-60"
        >
          <RefreshCw size={12} strokeWidth={1.6} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 rounded-3xl bg-white border border-blush-100 overflow-hidden">
          <div className="divide-y divide-blush-100 max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-ink-500 text-sm">Loading…</div>
            ) : list.length === 0 ? (
              <div className="p-10 text-center text-ink-500 font-body">
                <Mail size={28} strokeWidth={1.3} className="mx-auto text-blush-400 mb-3" />
                No submissions yet.
              </div>
            ) : list.map((s) => (
              <button
                key={s.id}
                onClick={() => { setSelected(s); if (!s.read) markRead(s); }}
                data-testid={`submission-row-${s.id}`}
                className={`w-full text-left p-4 hover:bg-blush-50/40 transition ${selected?.id === s.id ? "bg-blush-50/60" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className={`font-body text-sm ${s.read ? "text-ink-700" : "text-ink-900 font-medium"}`}>{s.name}</p>
                  {!s.read && <span className="h-2 w-2 rounded-full bg-blush-600 shrink-0 mt-2" />}
                </div>
                <p className="text-xs font-body text-ink-500 truncate mt-1">{s.email}</p>
                <p className="text-xs font-body text-ink-700 mt-1 line-clamp-1">{s.subject || s.message}</p>
                <p className="text-[10px] font-body text-ink-500 mt-2">{(s.created_at || "").slice(0, 16).replace("T", " · ")}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-3xl bg-white border border-blush-100 p-8 min-h-[70vh]">
          {!selected ? (
            <div className="h-full grid place-items-center text-center text-ink-500 font-body py-20">
              <div>
                <Mail size={40} strokeWidth={1.2} className="mx-auto text-blush-400 mb-3" />
                Select a message to read it.
              </div>
            </div>
          ) : (
            <div data-testid="submission-detail">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-ink-900">{selected.subject || "No subject"}</h2>
                  <p className="mt-1 font-body text-ink-700">{selected.name} · <a href={`mailto:${selected.email}`} className="text-blush-600 hover:underline">{selected.email}</a></p>
                  <p className="text-xs font-body text-ink-500 mt-1">{(selected.created_at || "").slice(0, 19).replace("T", " · ")}</p>
                </div>
                <div className="flex items-center gap-2">
                  {!selected.read && (
                    <button onClick={() => markRead(selected)} className="btn-ghost-rose text-xs inline-flex items-center gap-1">
                      <Check size={12} strokeWidth={1.6} /> Mark read
                    </button>
                  )}
                  <a href={`mailto:${selected.email}?subject=Re:%20${encodeURIComponent(selected.subject || "your message")}`} className="btn-maroon text-xs">Reply</a>
                  <button onClick={() => del(selected)} className="h-9 w-9 rounded-full grid place-items-center text-blush-600 hover:bg-blush-50" aria-label="Delete">
                    <Trash2 size={14} strokeWidth={1.6} />
                  </button>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-blush-100 whitespace-pre-wrap font-body text-ink-700 leading-relaxed">
                {selected.message}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
